/**
 * Centralized NDA display logic for project components.
 *
 * Multiple components (ProjectCard, PublicProjectLayout, PublicProjectHeader,
 * ProjectRequireNdaCard, ContactBusinessForm, ContactBusinessSuccess) each
 * independently implement the same NDA visibility rules. This utility
 * normalises the two data shapes (`Project` uses
 * `displayProjectNameBeforeNdaIsSigned`, while `ProjectTile` /
 * `ContactBusinessProjectForm` use `displayProjectName`) and returns a single
 * set of boolean flags that every consumer can rely on.
 *
 * It deliberately does NOT resolve i18n strings because each component uses a
 * different translation key for the obfuscated title.
 */

export interface NdaDisplayResult {
  /** Whether the project is NDA-protected. */
  isNda: boolean;
  /** Whether the project owner allows the project name to be displayed. */
  showProjectName: boolean;
  /** Whether to show the project location. */
  showLocation: boolean;
  /** Whether to show the market sector. */
  showSector: boolean;
  /** Whether to show the funding type. */
  showFundingType: boolean;
}

/**
 * Accepts any project-like object and returns NDA display flags.
 *
 * The function checks both `displayProjectName` (ES / ProjectTile) and
 * `displayProjectNameBeforeNdaIsSigned` (monolith / Project) so it works
 * with either data shape.
 */
export const getNdaProjectDisplay = (project: {
  requireNda?: boolean;
  displayProjectName?: boolean;
  displayProjectNameBeforeNdaIsSigned?: boolean;
  showLocationForNdaProjects?: boolean;
}): NdaDisplayResult => {
  const isNda = project?.requireNda === true;

  if (!isNda) {
    return {
      isNda: false,
      showProjectName: true,
      showLocation: true,
      showSector: true,
      showFundingType: true,
    };
  }

  // Normalise the two field names – prefer the ProjectTile field, fall back
  // to the Project (monolith) field.
  const ownerAllowsName =
    project.displayProjectName ??
    project.displayProjectNameBeforeNdaIsSigned ??
    false;

  return {
    isNda: true,
    showProjectName: ownerAllowsName,
    showLocation: project.showLocationForNdaProjects === true,
    showSector: false,
    showFundingType: false,
  };
};

/**
 * Resolves the display title for a project, applying NDA rules.
 *
 * Each component uses a different i18n string for the obfuscated NDA title,
 * so the caller passes that in as `ndaFallbackTitle`. The non-NDA title comes
 * from either `project.title` (ProjectTile / ContactBusinessProjectForm) or
 * `project.name` (Project from monolith) – the function checks both.
 */
export const resolveProjectTitle = (
  project: {
    projectName?: string;
    name?: string;
    title?: string;
  },
  ndaDisplay: NdaDisplayResult,
  ndaFallbackTitle: string
): string => {
  if (!ndaDisplay.isNda) {
    return project?.title ?? project?.name ?? '';
  }
  return ndaDisplay.showProjectName
    ? project?.projectName ?? ''
    : ndaFallbackTitle;
};
